import React from "react";
import { Link } from "react-router-dom";
import { X, Heart, ArrowRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useSEO } from "@/lib/seo";
import { Breadcrumbs, EmptyState } from "@/components/common/states";
import { priceRange, STATUS_LABEL } from "@/lib/constants";

const ROWS = [
  { label: "Price", get: (p) => priceRange(p) },
  { label: "Location", get: (p) => `${p.location_name}, Pune` },
  { label: "Configuration", get: (p) => p.configuration?.join(", ") || "—" },
  { label: "Area", get: (p) => `${p.area_min}-${p.area_max} ${p.area_unit}` },
  { label: "Status", get: (p) => STATUS_LABEL[p.status] || p.status },
  { label: "Possession", get: (p) => p.possession },
  { label: "Developer", get: (p) => p.developer_name },
  { label: "RERA", get: (p) => p.rera_number },
  { label: "Amenities", get: (p) => p.amenities?.slice(0, 6).join(", ") || "—" },
];

export default function Compare() {
  const { compare, toggleCompare, shortlist, toggleShortlist } = useApp();
  useSEO({ title: "Compare & Shortlist", description: "Compare up to 3 Pune properties side by side and review your shortlist.", path: "/compare" });

  return (
    <div className="ec-container py-8 sm:py-12">
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Compare & Shortlist" }]} />
      <h1 className="font-display text-3xl sm:text-4xl text-foreground mt-4 mb-8">Compare Properties</h1>

      {compare.length === 0 ? (
        <EmptyState title="No properties to compare"
          message="Add up to 3 properties using the Compare button on any property card."
          action={<Link to="/properties" className="h-11 px-6 inline-flex items-center rounded-md bg-primary text-primary-foreground text-sm font-semibold">Browse Properties</Link>} />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-white">
          <table data-testid="compare-table" className="w-full min-w-[640px] border-collapse">
            <thead>
              <tr>
                <th className="w-40 p-4 text-left align-bottom text-xs uppercase tracking-wider text-muted-foreground">Property</th>
                {compare.map((p) => (
                  <th key={p.slug} className="p-4 text-left align-top min-w-[220px]">
                    <div className="relative rounded-md overflow-hidden mb-3 h-32 bg-muted">
                      <img src={p.images?.[0]} alt={p.name} className="h-full w-full object-cover" />
                      <button data-testid={`compare-remove-${p.slug}`} onClick={() => toggleCompare(p)} aria-label={`Remove ${p.name}`}
                        className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/90 grid place-items-center hover:bg-white"><X className="h-4 w-4" aria-hidden /></button>
                    </div>
                    <Link to={`/properties/${p.slug}`} className="font-display text-base text-foreground hover:text-primary">{p.name}</Link>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.label} className="border-t border-border">
                  <td className="p-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold bg-muted/40">{r.label}</td>
                  {compare.map((p) => <td key={p.slug} className="p-4 text-sm text-foreground align-top">{r.get(p)}</td>)}
                </tr>
              ))}
              <tr className="border-t border-border">
                <td className="p-4 bg-muted/40" />
                {compare.map((p) => (
                  <td key={p.slug} className="p-4">
                    <Link to={`/properties/${p.slug}#enquire`} className="inline-flex items-center gap-1 text-sm font-semibold text-primary">Enquire <ArrowRight className="h-4 w-4" aria-hidden /></Link>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* SHORTLIST */}
      <div className="mt-16">
        <h2 className="font-display text-2xl text-foreground mb-6">Your Shortlist</h2>
        {shortlist.length === 0 ? (
          <p className="text-muted-foreground">You haven't shortlisted any properties yet. Tap the <Heart className="inline h-4 w-4 text-destructive" aria-hidden /> icon on a property to save it here.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {shortlist.map((s) => (
              <div key={s.slug} data-testid={`shortlist-item-${s.slug}`} className="flex gap-3 p-3 rounded-md border border-border bg-white">
                <img src={s.image} alt={s.name} className="h-20 w-24 rounded-sm object-cover" />
                <div className="flex-1 min-w-0">
                  <Link to={`/properties/${s.slug}`} className="font-medium text-foreground hover:text-primary line-clamp-1">{s.name}</Link>
                  <p className="text-xs text-muted-foreground">{s.location_name}</p>
                  <p className="text-sm text-primary mt-1">{s.price_display}</p>
                </div>
                <button onClick={() => toggleShortlist(s)} aria-label={`Remove ${s.name}`} className="text-muted-foreground hover:text-destructive self-start"><X className="h-4 w-4" aria-hidden /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
